import { yupResolver } from '@hookform/resolvers/yup';
import * as Api from '@ViewModels';
import { ICustomSurvey, IRichContentEditorState } from '@ViewModels';
import { FieldErrors, Resolver } from 'react-hook-form';
import * as yup from 'yup';
import {
	buildSurveyPartsSchema,
	createBaseSurveySchema,
	createCustomFormFieldSchema,
	createNotificationSettingsSchema,
	createTagGenerationSettingsSchema,
} from '../shared/validation/schemas';
import {
	isV2SurveyForm,
	normalizeSurveyPartsIntoSections,
} from '../shared/components/QuestionsWithSections/utils/transform';

// Form type that allows IRichContentEditorState for form fields but matches the expected structure
export type CustomSurveyFormData = Omit<Partial<ICustomSurvey>, 'options'> & {
	updateExistingContacts?: boolean;
	options?: {
		contactNameOptions?: Api.IContactFieldOptions;
		contactEmailOptions?: Api.IContactFieldOptions;
		contactPhoneNumberOptions?: Api.IContactFieldOptions;
		contactBirthdayOptions?: Api.IContactFieldOptions;
		contactAddressOptions?: Api.IContactFieldOptions;
		intro?: IRichContentEditorState;
		companyName?: string;
		sendConfirmationMessage?: boolean;
		includeCompanyLogo?: boolean;
		showSurveyName?: boolean;
		confirmationMessage?: IRichContentEditorState;
	};
	tagGenerationSettings?: {
		tagOnCompletion?: boolean;
		completionTag?: string;
		customFormFieldTagSettings?: {
			fieldId: string;
			shouldGenerateTag: boolean;
			tagPrefix: string;
		}[];
		effectiveCompletionTag?: Api.IAccountTag;
	};
	surveyFormVisibilityValidation?: {
		enabled: boolean;
		hasQuestion: boolean;
		hasAllAnswers: boolean;
	}[];
};

export const validationSchema = yup.object({
	// Base survey fields using shared schema (CustomSurvey doesn't require intro/dates)
	...createBaseSurveySchema({
		requireIntro: false,
		requireStartDate: false,
		requireExpirationDate: false,
	}),

	// CustomSurvey-specific options
	options: yup.object({
		contactNameOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactEmailOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactPhoneNumberOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactBirthdayOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactAddressOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		sendConfirmationMessage: yup.boolean().optional().default(false),
		intro: yup.mixed<IRichContentEditorState>().optional(),
		companyName: yup.string().optional(),
		includeCompanyLogo: yup.boolean().optional().default(false),
		showSurveyName: yup.boolean().optional().default(true),
		image: yup.string().optional(),
		confirmationMessage: yup.mixed<IRichContentEditorState>().optional(),
	}),

	// Custom form with CustomSurvey-specific requirements.
	//
	// Wrapped in yup.lazy so the customForm.fields label rule can read the sibling
	// surveyForm to decide V2 vs V1. V2 (sectioned) surveys store question labels in
	// surveyForm.surveyParts (validated via the surveyForm gate below); customForm.fields
	// is a derived mirror rebuilt from surveyParts only at save time, so during V2 editing
	// its labels are stale/empty. Requiring them throws a false "Question label is required"
	// that blocks save — so skip the mirror's label validation entirely for V2. V1 still
	// enforces it.
	//
	// The lazy resolver's second arg exposes `parent` (the root form value) — a top-level
	// lazy schema is rejected by yupResolver's types, so gating happens here at the field.
	customForm: yup.lazy((_value, options) => {
		const root = (options as { parent?: { surveyForm?: Parameters<typeof isV2SurveyForm>[0] } })?.parent;
		const isV2 = isV2SurveyForm(root?.surveyForm);
		return yup.object({
			name: yup.string(),
			startDate: yup.date(),
			fields: createCustomFormFieldSchema({
				requireLabels: true, // CustomSurvey allows flexible label requirements
				skipLabelValidation: isV2,
			}),
		});
	}),

	// Shared notification settings (no ratings for CustomSurvey)
	notificationSettings: createNotificationSettingsSchema().optional(),

	// Shared tag generation settings
	tagGenerationSettings: createTagGenerationSettingsSchema().optional(),

	// CustomSurvey-specific fields
	hasDateRange: yup.boolean().optional().default(false),
	allowPartialSubmission: yup.boolean().optional().default(false),

	// SurveyForm — V2 path validates surveyParts; V1 path falls through as mixed.
	// V1 surveys populate surveyForm.surveyParts with reference-only objects
	// (`{ id, _type, fieldId, visibleWhen }` — no `label`/`options`) for visibility
	// tracking only; V1's actual validation lives in createCustomFormFieldSchema
	// over `customForm.fields`. Applying the V2 schema unconditionally renders
	// every V1 survey unsaveable, so gate on shape.
	surveyForm: yup.lazy(value =>
		isV2SurveyForm(value as Parameters<typeof isV2SurveyForm>[0])
			? yup
					.object({
						isEnabled: yup.boolean().optional(),
						surveyParts: buildSurveyPartsSchema({ requireLabels: true }).optional(),
					})
					.optional()
			: yup.mixed().optional()
	),

	// Per-field conditional visibility validation entries
	// Parallel array matching customForm.fields, used to validate incomplete visibility configs
	surveyFormVisibilityValidation: yup
		.array()
		.of(
			yup
				.object({
					enabled: yup.boolean(),
					hasQuestion: yup.boolean(),
					hasAllAnswers: yup.boolean(),
				})
				.test(
					'conditional-visibility-complete',
					'Please select a question and answer for conditional visibility',
					function (value) {
						if (!value?.enabled) return true;
						if (!value.hasQuestion) {
							return this.createError({ message: 'Please select a question for conditional visibility' });
						}
						if (!value.hasAllAnswers) {
							return this.createError({ message: 'Please select an answer for each conditional visibility row' });
						}
						return true;
					}
				)
		)
		.optional(),
});

/**
 * Validates the shape that will actually be sent (see `ensureSurveyFormEnabled`), so a part
 * the editor cannot render can never block the save with an unreachable error.
 */
const baseResolver = yupResolver(validationSchema);

export const customSurveyResolver: Resolver<CustomSurveyFormData> = async (values, context, options) => {
	const surveyForm = values.surveyForm as Api.SurveyForm | undefined;
	const normalized = surveyForm ? normalizeSurveyPartsIntoSections(surveyForm) : undefined;
	if (!normalized || normalized === surveyForm) {
		return baseResolver(values, context, options);
	}

	const result = await baseResolver({ ...values, surveyForm: normalized }, context, options);
	return {
		...result,
		errors: remapSurveyPartErrors(result.errors, surveyForm.surveyParts, normalized.surveyParts),
	};
};

/** Normalizing renumbers the array; section cards read errors by raw position, so map them back. */
const remapSurveyPartErrors = (
	errors: FieldErrors<CustomSurveyFormData>,
	rawParts: ReadonlyArray<{ id?: string } | undefined | null>,
	normalizedParts: ReadonlyArray<{ id?: string } | undefined | null>
): FieldErrors<CustomSurveyFormData> => {
	const partErrors = (errors as { surveyForm?: { surveyParts?: unknown[] } })?.surveyForm?.surveyParts;
	if (!partErrors) {
		return errors;
	}

	const remapped: unknown[] = [];
	normalizedParts.forEach((part, normalizedIndex) => {
		const error = partErrors[normalizedIndex];
		if (!error) {
			return;
		}
		const rawIndex = rawParts.findIndex(raw => raw?.id && raw.id === part?.id);
		remapped[rawIndex >= 0 ? rawIndex : normalizedIndex] = error;
	});

	return {
		...errors,
		surveyForm: { ...(errors as { surveyForm?: object }).surveyForm, surveyParts: remapped },
	} as FieldErrors<CustomSurveyFormData>;
};

/** First leaf message in an RHF error tree, with the dotted path it sits on. */
export const findFirstError = (
	errors: unknown,
	path: string[] = [],
	// RHF hangs DOM nodes off `ref`, which loop back into React internals.
	seen = new WeakSet<object>()
): { message: string; path: string } | null => {
	if (!errors || typeof errors !== 'object' || seen.has(errors)) {
		return null;
	}
	seen.add(errors);
	const node = errors as { message?: unknown };
	if (typeof node.message === 'string' && node.message.length > 0) {
		return { message: node.message, path: path.join('.') };
	}
	for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
		const found = findFirstError(value, [...path, key], seen);
		if (found) {
			return found;
		}
	}
	return null;
};

export const TabValues = {
	Questions: 'Questions',
	Settings: 'Settings',
	Preview: 'Preview',
} as const;

export type TabValues = (typeof TabValues)[keyof typeof TabValues];
